import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ContentEntry } from '../../content/siteContent';
import { COLORS, SPACING } from '../../theme/designTokens';
import {
  ENTWICKLUNGEN_CARD_GAP,
  ENTWICKLUNGEN_CAROUSEL_STEP,
  ENTWICKLUNGEN_MOBILE_PEEK,
  OVERVIEW_CARD_MD_HEIGHT,
  OVERVIEW_CARD_MD_WIDTH,
  entwicklungenCardBodySx,
  entwicklungenCardSx,
  entwicklungenCardTitleSx,
} from '../../theme/overviewCardStyles';

const SCROLL_EDGE_THRESHOLD = 8;

type ArrowSide = 'left' | 'right';

const CarouselArrow: React.FC<{
  side: ArrowSide;
  label: string;
  onClick: () => void;
}> = ({ side, label, onClick }) => {
  const isLeft = side === 'left';

  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      onClick={onClick}
      sx={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        ...(isLeft ? { left: 0 } : { right: SPACING.sectionPaddingX }),
        zIndex: 4,
        width: 32,
        height: 32,
        p: 0,
        border: 'none',
        borderRadius: 0,
        backgroundColor: 'transparent',
        color: COLORS.text,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: isLeft ? 'center' : 'flex-end',
        transition: 'color 0.35s ease, transform 0.35s ease',
        '&:hover': {
          color: COLORS.bgDark,
          transform: 'translateY(-50%) scale(1.08)',
        },
        '&:focus-visible': {
          outline: `2px solid ${COLORS.bgDark}`,
          outlineOffset: 4,
        },
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 24 24"
        aria-hidden
        sx={{ width: 22, height: 22, display: 'block', pointerEvents: 'none' }}
      >
        <path
          d={isLeft ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Box>
    </Box>
  );
};

interface EntwicklungenCarouselProps {
  items: ContentEntry[];
  prevLabel: string;
  nextLabel: string;
}

const EntwicklungenCarousel: React.FC<EntwicklungenCarouselProps> = ({
  items,
  prevLabel,
  nextLabel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const clipRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [mobileCardWidth, setMobileCardWidth] = useState<number | null>(null);

  const measureMobileCardWidth = useCallback(() => {
    const vw = window.innerWidth;
    if (!vw) return;
    setMobileCardWidth(vw - ENTWICKLUNGEN_MOBILE_PEEK - ENTWICKLUNGEN_CARD_GAP);
  }, []);

  const getScrollStep = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return ENTWICKLUNGEN_CAROUSEL_STEP;
    const card = el.querySelector<HTMLElement>('[data-carousel-card]');
    if (!card) return ENTWICKLUNGEN_CAROUSEL_STEP;
    return card.offsetWidth + ENTWICKLUNGEN_CARD_GAP;
  }, []);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > SCROLL_EDGE_THRESHOLD);
    setCanScrollRight(
      scrollLeft + clientWidth < scrollWidth - SCROLL_EDGE_THRESHOLD,
    );
  }, []);

  useEffect(() => {
    measureMobileCardWidth();

    const ro = new ResizeObserver(() => {
      measureMobileCardWidth();
      updateScrollState();
    });

    if (clipRef.current) ro.observe(clipRef.current);
    return () => ro.disconnect();
  }, [measureMobileCardWidth, updateScrollState]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollLeft = 0;
    updateScrollState();

    const onScroll = () => updateScrollState();
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => el.removeEventListener('scroll', onScroll);
  }, [items.length, mobileCardWidth, updateScrollState]);

  const scrollByStep = (direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;

    el.scrollTo({
      left: el.scrollLeft + direction * getScrollStep(),
      behavior: 'smooth',
    });
  };

  return (
    <Box
      ref={clipRef}
      sx={{
        position: 'relative',
        mx: {
          xs: `calc(-1 * ${SPACING.sectionPaddingX.xs})`,
          sm: `calc(-1 * ${SPACING.sectionPaddingX.sm})`,
          md: `calc(-1 * ${SPACING.sectionPaddingX.md})`,
        },
      }}
    >
      {canScrollLeft && (
        <CarouselArrow
          side="left"
          label={prevLabel}
          onClick={() => scrollByStep(-1)}
        />
      )}
      {canScrollRight && (
        <CarouselArrow
          side="right"
          label={nextLabel}
          onClick={() => scrollByStep(1)}
        />
      )}

      <Box
        ref={scrollRef}
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollSnapType: 'x mandatory',
          scrollPaddingLeft: SPACING.sectionPaddingX,
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          touchAction: 'pan-x',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: `${ENTWICKLUNGEN_CARD_GAP}px`,
            width: 'max-content',
            py: '4px',
            pl: SPACING.sectionPaddingX,
            pr: SPACING.sectionPaddingX,
          }}
        >
          {items.map((card, index) => (
            <Box
              key={card.slug}
              data-carousel-card
              sx={{
                ...entwicklungenCardSx,
                position: 'relative',
                zIndex: 1,
                scrollSnapAlign: 'start',
                boxSizing: 'border-box',
                width: isMobile
                  ? mobileCardWidth
                    ? `${mobileCardWidth}px`
                    : '85vw'
                  : OVERVIEW_CARD_MD_WIDTH,
                minWidth: isMobile
                  ? mobileCardWidth
                    ? `${mobileCardWidth}px`
                    : '85vw'
                  : OVERVIEW_CARD_MD_WIDTH,
                maxWidth: isMobile
                  ? mobileCardWidth
                    ? `${mobileCardWidth}px`
                    : '85vw'
                  : OVERVIEW_CARD_MD_WIDTH,
                height: { xs: 'auto', md: OVERVIEW_CARD_MD_HEIGHT },
                minHeight: { xs: 280, md: OVERVIEW_CARD_MD_HEIGHT },
                flexShrink: 0,
                opacity: 1,
                transition: 'opacity 0.35s ease',
                cursor: 'default',
              }}
            >
              <Typography sx={entwicklungenCardTitleSx}>{card.title}</Typography>
              <Typography sx={entwicklungenCardBodySx}>
                {card.description}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default EntwicklungenCarousel;
